import { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '@/lib/backendUrl';
import DayCircle from '@/components/daily3/DayCircle';

/**
 * «Твой день» на /transits/results — КРУГ ДНЯ (components/daily3/DayCircle).
 *
 * Здесь — загрузка ленты (POST /api/daily/timeline: раскладку и лесенку
 * тарифа отдаёт сервер, services/day_timeline.py поверх прогноза письма),
 * стрелка «сейчас» и связка с разбором: тап по карточке в разборе
 * подсвечивает её точку / обводку на круге, «В разборе ↓» во всплывашке
 * круга ведёт к карточке. В PDF не попадает (data-pdf-exclude).
 */

// Подсветка связки у карточки в разборе.
export const LINK_RING = ['ring-2', 'ring-violet-400', 'ring-offset-2'];

function viewerTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_e) {
    return null;
  }
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

// Сейчас в поясе ленты: { date: 'YYYY-MM-DD', minutes }.
export function nowIn(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || undefined, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (t) => (parts.find((p) => p.type === t) || {}).value;
    return {
      date: `${get('year')}-${get('month')}-${get('day')}`,
      minutes: Number(get('hour')) * 60 + Number(get('minute')),
    };
  } catch (_e) {
    const d = new Date();
    return {
      date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
      minutes: d.getHours() * 60 + d.getMinutes(),
    };
  }
}

// Данные рождения из браузера — их кладёт форма калькулятора (и главная).
function storedBirth() {
  try {
    const b = JSON.parse(localStorage.getItem('birthData') || 'null');
    const n = (b && b.natal) || {};
    if (!n.date || n.latitude == null || n.longitude == null) return {};
    return {
      birth_date: n.date,
      birth_time: n.time || null,
      birth_time_unknown: !!n.time_unknown,
      latitude: n.latitude,
      longitude: n.longitude,
      timezone: n.timezone || null,
    };
  } catch (_e) {
    return {};
  }
}

// Прокрутка без анимации: плавная (scroll-behavior: smooth на html) идёт по
// кадрам и стоит на месте во вкладке без отрисовки. Переход мгновенный, и
// если страница всё же не доехала — ставим позицию напрямую.
/** Лента дня с сервера (по уровню доступа). Нужна и без самого круга:
 *  гостю страница «События на день» берёт из неё только «Фокус дня». */
export function fetchTimeline(date) {
  const token = localStorage.getItem('auth_token');
  return fetch(`${API_URL}/daily/timeline/${date}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ ...storedBirth(), viewer_timezone: viewerTimezone() }),
  }).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))));
}

export function jumpTo(el, block = 'center') {
  if (!el) return;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  let top = window.scrollY + r.top;
  if (block === 'center') top -= Math.max(0, (vh - r.height) / 2);
  else if (block === 'nearest') {
    if (r.top >= 0 && r.bottom <= vh) return;
    top = r.top < 0 ? top - 16 : window.scrollY + r.bottom - vh + 16;
  }
  top = Math.max(0, Math.round(top));
  window.scrollTo({ top, behavior: 'instant' });
  if (Math.abs(window.scrollY - top) > 2) {
    const se = document.scrollingElement || document.documentElement;
    se.scrollTop = top;
  }
}

// Стрелка «сейчас» — только в сегодняшнем дне, по поясу ленты.
export function useNowMinutes(data) {
  const [now, setNow] = useState(null);
  useEffect(() => {
    if (!data) return undefined;
    const tick = () => {
      const n = nowIn(data.timezone);
      setNow(n.date === data.date ? n.minutes : null);
    };
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [data]);
  return now;
}

export default function DayTimeline({
  date, isAuthenticated, onRegister, onLoaded, linkKey, onGoTo, hasTarget,
}) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;
    setFailed(false);
    fetchTimeline(date)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        if (onLoaded) onLoaded(d);
      })
      .catch(() => { if (!cancelled) { setFailed(true); setData(null); } });
    return () => { cancelled = true; };
  }, [date, isAuthenticated, onLoaded]);

  const now = useNowMinutes(data);

  // Связка из разбора: есть ли на круге то, что подсветили внизу.
  const linkedOnCircle = useMemo(() => {
    if (!data || !linkKey) return false;
    const inMood = (data.mood.hours || []).some((h) => h.items.some((x) => x.key === linkKey));
    const inEvents = (data.events.markers || []).some((mk) => mk.items.some((x) => x.key === linkKey));
    const inBg = (data.destiny.items || []).slice(0, 3).some((x) => x.key === linkKey);
    return inMood || inEvents || inBg;
  }, [data, linkKey]);

  useEffect(() => {
    if (linkedOnCircle && rootRef.current) jumpTo(rootRef.current, 'nearest');
  }, [linkKey, linkedOnCircle]);

  if (failed || !data) {
    if (failed || !date) return null;
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-500"
        data-testid="timeline-loading" data-pdf-exclude>
        Раскладываю твой день по часам…
      </div>
    );
  }

  return (
    <section ref={rootRef} className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5"
      data-testid="day-timeline" data-pdf-exclude>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-base font-bold text-gray-800">Твой день по часам</h2>
        <p className="text-xs text-gray-400">по местному времени</p>
      </div>
      <DayCircle data={data} now={now} linkKey={linkKey} onRegister={onRegister}
        onGoTo={onGoTo} hasTarget={hasTarget} />
    </section>
  );
}
